import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, MapPin, Check } from "lucide-react";
import { PortalHeader, PortalCard } from "@/components/portal/PortalHeader";
import { getMyProfile, updateMyProfile } from "@/lib/portal/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const qc = useQueryClient();
  const getFn = useServerFn(getMyProfile);
  const saveFn = useServerFn(updateMyProfile);
  const q = useQuery({ queryKey: ["portal", "profile"], queryFn: () => getFn() });

  const [form, setForm] = useState({
    display_name: "", phone: "",
    comm_email: true, comm_sms: false, comm_push: true,
    secondary_name: "", secondary_phone: "", secondary_email: "", secondary_rel: "",
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!q.data) return;
    const p = q.data.preferences as any;
    const sc = (p.secondary_contact ?? {}) as any;
    setForm({
      display_name: p.display_name ?? "", phone: p.phone ?? "",
      comm_email: p.comm_email ?? true, comm_sms: p.comm_sms ?? false, comm_push: p.comm_push ?? true,
      secondary_name: sc.name ?? "", secondary_phone: sc.phone ?? "",
      secondary_email: sc.email ?? "", secondary_rel: sc.relationship ?? "",
    });
  }, [q.data]);

  const submit = async () => {
    setBusy(true);
    try {
      await saveFn({ data: {
        display_name: form.display_name, phone: form.phone || undefined,
        comm_email: form.comm_email, comm_sms: form.comm_sms, comm_push: form.comm_push,
        secondary_contact: {
          name: form.secondary_name || undefined, phone: form.secondary_phone || undefined,
          email: form.secondary_email || undefined, relationship: form.secondary_rel || undefined,
        },
      } });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      await qc.invalidateQueries({ queryKey: ["portal", "profile"] });
      await qc.invalidateQueries({ queryKey: ["portal", "project"] });
    } catch (e: any) { alert(e?.message || "Save failed"); }
    finally { setBusy(false); }
  };

  if (q.isLoading || !q.data) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  const property = q.data.property;

  return (
    <>
      <PortalHeader eyebrow="Profile" title="Account & preferences" description="Update how we reach you and who to contact if you're not available." />

      <div className="space-y-6">
        <Section title="Your details">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name"><input className={inputCls} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></Field>
            <Field label="Phone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email">
              <input className={`${inputCls} opacity-60 cursor-not-allowed`} value={q.data.email ?? ""} disabled />
              <p className="text-[10px] text-zinc-500 mt-1">Contact us to change your sign-in email.</p>
            </Field>
          </div>
        </Section>

        <Section title="Notification preferences">
          <div className="space-y-2">
            <Toggle label="Email updates" hint="Schedule changes, new messages, proposal updates" checked={form.comm_email} onChange={(v) => setForm({ ...form, comm_email: v })} />
            <Toggle label="SMS reminders" hint="Day-before reminders for appointments" checked={form.comm_sms} onChange={(v) => setForm({ ...form, comm_sms: v })} />
            <Toggle label="In-app notifications" hint="Show alerts in your portal dashboard" checked={form.comm_push} onChange={(v) => setForm({ ...form, comm_push: v })} />
          </div>
        </Section>

        <Section title="Secondary contact" description="Spouse, partner, property manager — anyone we should reach if we can't reach you.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><input className={inputCls} value={form.secondary_name} onChange={(e) => setForm({ ...form, secondary_name: e.target.value })} /></Field>
            <Field label="Relationship"><input className={inputCls} value={form.secondary_rel} onChange={(e) => setForm({ ...form, secondary_rel: e.target.value })} placeholder="Spouse, partner…" /></Field>
            <Field label="Phone"><input className={inputCls} value={form.secondary_phone} onChange={(e) => setForm({ ...form, secondary_phone: e.target.value })} /></Field>
            <Field label="Email"><input className={inputCls} type="email" value={form.secondary_email} onChange={(e) => setForm({ ...form, secondary_email: e.target.value })} /></Field>
          </div>
        </Section>

        {property && (
          <Section title="Property">
            <div className="flex items-start gap-3">
              <MapPin className="size-5 text-amber-300 shrink-0 mt-0.5" />
              <div>
                <p className="text-white">{property.address || "—"}</p>
                <p className="text-sm text-zinc-400">{[property.city, property.state, property.zip].filter(Boolean).join(", ")}</p>
                <p className="text-[11px] text-zinc-500 mt-1">{property.project_name}</p>
                <p className="text-[10px] text-zinc-400 mt-2">Address changes require your project manager.</p>
              </div>
            </div>
          </Section>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          {saved && <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><Check className="size-3.5" /> Saved</span>}
          <button onClick={submit} disabled={busy} className="bg-amber-300 hover:bg-amber-200 text-zinc-950 text-sm font-semibold px-6 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="size-4 animate-spin" />} Save changes
          </button>
        </div>
      </div>
    </>
  );
}

const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-[15px] text-white focus:border-amber-300/50 outline-none";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <PortalCard className="p-6">
      <h2 className="font-serif text-xl text-white">{title}</h2>
      {description && <p className="text-xs text-zinc-500 mt-1">{description}</p>}
      <div className="mt-4">{children}</div>
    </PortalCard>
  );
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start justify-between gap-4 py-2 cursor-pointer">
      <div>
        <p className="text-sm text-white">{label}</p>
        {hint && <p className="text-[11px] text-zinc-500 mt-0.5">{hint}</p>}
      </div>
      <button type="button" onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full shrink-0 transition ${checked ? "bg-amber-300" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 left-0.5 size-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}